export type SnippetProduct = 'CONFLUENCE' | 'JIRA' | string;
export type SnippetStatus = 'active' | 'deleted' | string;

export type Snippet = {
	resourceId: string;
	snippetId: string;
	name: string;
	product: SnippetProduct;
	status: SnippetStatus;
	createdBy: string;
	createdAt: number;
	updatedAt: number;
	content: string;
	contentType: string;
};

const createTextNode = (text: string) => ({ type: 'text', text });
const createParagraph = (text: string) => ({
	type: 'paragraph',
	content: [createTextNode(text)],
});
const createHeading = (level: number, text: string) => ({
	type: 'heading',
	attrs: { level },
	content: [createTextNode(text)],
});
const createDoc = (content: any[]) => ({
	type: 'doc',
	version: 1,
	content,
});
const createListItem = (text: string) => ({
	type: 'listItem',
	content: [createParagraph(text)],
});
const createBulletList = (items: string[]) => ({
	type: 'bulletList',
	content: items.map(createListItem),
});
const createTaskItem = (localId: string, text: string) => ({
	type: 'taskItem',
	attrs: { localId, state: 'TODO' },
	content: [createTextNode(text)],
});
const createTaskList = (tasks: Array<{ id: string; text: string }>) => ({
	type: 'taskList',
	content: tasks.map((task) => createTaskItem(task.id, task.text)),
});

export async function fetchSnippetsFromAPI(): Promise<Snippet[]> {
	try {
		const now = Date.now();
		const snippetData = [
			{
				id: 123,
				name: 'Snippet 1 with doc type',
				createdBy: 'user-123',
				ageMs: 86400000,
				content: createDoc([
					createHeading(2, 'Meeting Notes'),
					createParagraph('Date: '),
					createParagraph('Attendees: '),
					createHeading(3, 'Agenda'),
					createBulletList(['Item 1']),
					createHeading(3, 'Action Items'),
					createBulletList(['Action item']),
				]),
			},
			{
				id: 125,
				name: 'Snippet 2 with doc type task list and a very very long title that will be truncated',
				createdBy: 'user-125',
				ageMs: 86400000,
				content: createDoc([
					createHeading(2, 'Code Review Checklist'),
					createTaskList([
						{ id: 'task-1', text: 'Code follows style guidelines' },
						{ id: 'task-2', text: 'Tests are included and passing' },
					]),
				]),
			},
			{
				id: 126,
				name: 'Snippet 3 with fragment type',
				createdBy: 'user-126',
				ageMs: 345600000,
				content: [
					createParagraph('This is a fragment - an array of nodes without a doc wrapper.'),
					createParagraph('It will be inserted as-is.'),
				],
			},
			{
				id: 127,
				name: 'Snippet 4 with single node type',
				createdBy: 'user-127',
				ageMs: 432000000,
				content: createParagraph(
					'This is a single node snippet - just one paragraph node, not wrapped in a doc.',
				),
			},
			{
				id: 128,
				name: 'Snippet 5 with doc type and undefined nodes',
				createdBy: 'user-128',
				ageMs: 518400000,
				content: createDoc([
					createParagraph('First paragraph'),
					null,
					createParagraph('Second paragraph'),
					null,
					createHeading(2, 'Heading after null values'),
				]),
			},
		];

		const mockSnippets: Snippet[] = snippetData.map((data) => {
			const timestamp = now - data.ageMs;
			return {
				resourceId: `ari:cloud:blocks:mock-cloud-id:snippet/workspace/workspace-id/${data.id}`,
				snippetId: `snippet-${data.id}`,
				name: data.name,
				content: JSON.stringify(data.content),
				contentType: 'ADF',
				product: 'CONFLUENCE',
				status: 'active',
				createdBy: data.createdBy,
				createdAt: timestamp,
				updatedAt: timestamp,
			};
		});

		return mockSnippets;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Failed to fetch snippets:', error);
		return [];
	}
}
